import React, { useEffect, useState } from 'react';
import {
    ModalOverlay,
    ModalContent,
    CloseButton,
    ModalTitle,
    TabContainer,
    TabList,
    Tab,
    TabContent,
    ModalHeader,
} from './style';
import useDragAndDrop from '../../hooks/useDragAndDrop';
import axios from 'axios';
import { API_URL } from 'config/URL/URL';
import HousingChart from './HousingChart';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart as BarChartIcon, TableChart as TableChartIcon } from '@mui/icons-material';
import HousingTable from './HousingTable';

const HousingChartModal = ({ isOpen, onClose, layerName, pointParams, selectedLocation }) => {
    const [ activeTab, setActiveTab ] = useState('trade');
    const [ apiData, setApiData ] = useState();
    
    const { modalRef, position, isVisible, handleDragStart } = useDragAndDrop(isOpen && apiData);
    const [ viewMode, setViewMode ] = useState('chart');

    useEffect(() => {
        if(isOpen && selectedLocation) {
            fetchApiData(layerName, pointParams, selectedLocation);
        }
        
        return () => {
            setActiveTab('trade');
            setViewMode('chart');
            setApiData();
        }
    }, [isOpen, selectedLocation])

    const fetchApiData = async (layerName, pointParams, selectedLocation) => {
        const newData = await getHousingDetailData(layerName, pointParams, selectedLocation);    

        setApiData({
            trdDealInfo: {
                dataName: 'trdDealInfo',
                seriesOptions: [
                    {
                        key: 'avg_amount',
                        name: '매매 평균가',
                        unit: '만원'
                    }, {
                        key: 'cnt',
                        name: '거래량',
                        unit: '호'
                    }
                ],
                chartData: newData.map(({ ymd, trd_avg_amount, trd_cnt }) => ({ ymd, avg_amount: trd_avg_amount, cnt: trd_cnt }))
            },            
            rentDealInfo: {
                dataName: 'rentDealInfo',
                seriesOptions: [
                    {
                        key: 'avg_amount',
                        name: '환산보증금 평균가',
                        unit: '만원'
                    }, {
                        key: 'cnt',
                        name: '거래량',
                        unit: '호'
                    }, {
                        key: 'deposit_rate',
                        name: '전세가율',
                        unit: '%'
                    }
                ],
                chartData: newData.map(({ ymd, rent_avg_amount, rent_cnt, deposit_rate }) => ({ ymd, avg_amount: rent_avg_amount, cnt: rent_cnt, deposit_rate })),
            },
        });
    }


    const getHousingDetailData = async (layerName, pointParams, selectedLocation) => {
        const options = {
            params: {
                layerName: layerName,
                apt: pointParams.apartment,
                dsd: pointParams.multiFamily,
                off: pointParams.officetel,
                min: pointParams.minArea,
                max: pointParams.maxArea,
                hjdCd: selectedLocation.hjdCd
            }
        }

        try {
            const response = await axios.get(`${API_URL}/house/getDealInfo`, options);
            
            return response.data;
        } catch(error) {
            console.log(error);
        }
    }

        const handleViewModeChange = (event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    if(apiData)
    return (
        <ModalOverlay>
            <ModalContent
                ref={modalRef}
                style={{
                    left: position.x,
                    top: position.y,
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 0.1s ease-in-out',
                    pointerEvents: isVisible ? 'auto' : 'none',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <ModalHeader onMouseDown={handleDragStart} onTouchStart={handleDragStart}>
                    <ModalTitle>{selectedLocation?.hjdNm} 주택현황</ModalTitle>
                    <CloseButton
                        onClick={onClose}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                    >
                        &times;
                    </CloseButton>
                </ModalHeader>

                <TabContainer>
                    <TabList>
                        <Tab className={activeTab === 'trade' ? 'active' : ''} onClick={() => setActiveTab('trade')}>
                            매매 거래
                        </Tab>
                        <Tab className={activeTab === 'rent' ? 'active' : ''} onClick={() => setActiveTab('rent')}>
                            전월세 거래
                        </Tab>
                    </TabList>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={handleViewModeChange}
                            aria-label="view mode"
                            size="small"
                        >
                            <ToggleButton value="chart" aria-label="chart view">
                                <BarChartIcon sx={{ mr: 1 }} />
                                차트
                            </ToggleButton>
                            <ToggleButton value="table" aria-label="table view">
                                <TableChartIcon sx={{ mr: 1 }} />
                                테이블
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <TabContent active={activeTab === 'trade'}>
                        {
                            viewMode === 'chart' ? 
                            <HousingChart
                                data={apiData.trdDealInfo}
                                title={`${selectedLocation?.hjdNm} 매매 거래량 및 가격추이(${pointParams.minArea}~${pointParams.maxArea}m²)`}
                            /> :
                            <HousingTable 
                                data={apiData.trdDealInfo}
                                title={`${selectedLocation?.hjdNm} 매매 거래량 및 가격추이(${pointParams.minArea}~${pointParams.maxArea}m²)`}
                            />
                        }

                    </TabContent>

                    <TabContent active={activeTab === 'rent'}>
                        {
                            viewMode === 'chart' ?
                            <HousingChart
                                data={apiData.rentDealInfo}
                                title={`${selectedLocation?.hjdNm} 전월세 거래량 및 가격추이(${pointParams.minArea}~${pointParams.maxArea}m²)`}
                            /> :
                            <HousingTable 
                                data={apiData.rentDealInfo}
                                title={`${selectedLocation?.hjdNm} 전월세 거래량 및 가격추이(${pointParams.minArea}~${pointParams.maxArea}m²)`}
                            />    

                        }

                    </TabContent>
                </TabContainer>
            </ModalContent>
        </ModalOverlay>
    );
};

export default HousingChartModal;